import { BREAKPOINTS } from '../../../utils/breakpoints';

import type { FloatingConfig, FloatingPosition } from './ConfigProvider';

const DEFAULT_MAX_HEIGHT = 400;
const DEFAULT_MAX_WIDTH = 300;
const DEFAULT_OFFSET = 16;
const DEFAULT_POSITION: FloatingPosition = 'bottom-right';

export interface FloatingCssVars {
  '--floating-width': string;
  '--floating-height': string;
  '--floating-inset-block-start': string;
  '--floating-inset-block-end': string;
  '--floating-inset-inline-start': string;
  '--floating-inset-inline-end': string;
}

const fitBox = (aspectRatio: number, maxWidth: number, maxHeight: number) => {
  const widthFromHeight = aspectRatio * maxHeight;
  if (widthFromHeight <= maxWidth) {
    return { width: widthFromHeight, height: maxHeight };
  }
  return { width: maxWidth, height: maxWidth / aspectRatio };
};

export const resolveFloatingDimensions = ({
  aspectRatio,
  isMobile,
  maxWidth,
  maxHeight,
  viewportWidth,
  offset,
}: {
  aspectRatio: number;
  isMobile: boolean;
  maxWidth: number;
  maxHeight: number;
  viewportWidth: number;
  offset: number;
}): { width: number; height: number } => {
  if (!isMobile) {
    return fitBox(aspectRatio, maxWidth, maxHeight);
  }

  const isPortrait = aspectRatio < 1;
  let width = isPortrait
    ? Math.max(0, viewportWidth / 2 - offset)
    : Math.max(0, viewportWidth - offset * 2);
  let height = width / aspectRatio;

  if (width > maxWidth) {
    width = maxWidth;
    height = width / aspectRatio;
  }
  if (height > maxHeight) {
    height = maxHeight;
    width = height * aspectRatio;
  }

  return { width, height };
};

export const computeFloatingCssVars = ({
  width,
  height,
  offset,
  blockPosition,
  inlinePosition,
}: {
  width: number;
  height: number;
  offset: number;
  blockPosition: 'top' | 'bottom';
  inlinePosition: 'left' | 'right';
}): FloatingCssVars => {
  const offsetPx = `${offset}px`;

  return {
    '--floating-width': `${width}px`,
    '--floating-height': `${height}px`,
    '--floating-inset-block-start': blockPosition === 'top' ? offsetPx : 'auto',
    '--floating-inset-block-end':
      blockPosition === 'bottom' ? offsetPx : 'auto',
    '--floating-inset-inline-start':
      inlinePosition === 'left' ? offsetPx : 'auto',
    '--floating-inset-inline-end':
      inlinePosition === 'right' ? offsetPx : 'auto',
  };
};

export const applyCssVars = (element: HTMLElement, vars: FloatingCssVars) => {
  for (const [name, value] of Object.entries(vars)) {
    element.style.setProperty(name, value);
  }
};

export const resolveFloatingConfig = (config: FloatingConfig | undefined) => ({
  maxHeight: config?.maxHeight ?? DEFAULT_MAX_HEIGHT,
  maxWidth: config?.maxWidth ?? DEFAULT_MAX_WIDTH,
  verticalMaxWidth: config?.verticalMaxWidth,
  verticalMaxHeight: config?.verticalMaxHeight,
  offset: config?.offset ?? DEFAULT_OFFSET,
  position: config?.position ?? DEFAULT_POSITION,
  mobileBreakpoint: config?.mobile?.breakpoint ?? BREAKPOINTS['tablet'],
  mobilePosition: config?.mobile?.position ?? ('top' as const),
  mobileVerticalAlign: config?.mobile?.verticalAlign ?? ('right' as const),
  mobileMaxWidth: config?.mobile?.maxWidth,
  mobileMaxHeight: config?.mobile?.maxHeight,
  mobileVerticalMaxWidth: config?.mobile?.verticalMaxWidth,
  mobileVerticalMaxHeight: config?.mobile?.verticalMaxHeight,
});

export type ResolvedFloatingConfig = ReturnType<typeof resolveFloatingConfig>;

export const resolveFloatingConstraints = ({
  resolved,
  isMobile,
  isPortrait,
  isAd,
}: {
  resolved: ResolvedFloatingConfig;
  isMobile: boolean;
  isPortrait: boolean;
  isAd: boolean;
}): { maxWidth: number; maxHeight: number } => {
  if (isMobile) {
    if (isAd) return { maxWidth: Infinity, maxHeight: Infinity };
    return {
      maxWidth: isPortrait
        ? (resolved.mobileVerticalMaxWidth ??
          resolved.mobileMaxWidth ??
          Infinity)
        : (resolved.mobileMaxWidth ?? Infinity),
      maxHeight: isPortrait
        ? (resolved.mobileVerticalMaxHeight ??
          resolved.mobileMaxHeight ??
          Infinity)
        : (resolved.mobileMaxHeight ?? Infinity),
    };
  }
  return {
    maxWidth: isPortrait
      ? (resolved.verticalMaxWidth ?? resolved.maxWidth)
      : resolved.maxWidth,
    maxHeight: isPortrait
      ? (resolved.verticalMaxHeight ?? resolved.maxHeight)
      : resolved.maxHeight,
  };
};
